import { APP_EMITTER_EVENTS, AppEmitter, ToastContainer, useDebounce } from "shared";
import { DebugPanel } from "features/debug-panel";
import React, { useEffect, useRef, useState } from "react";
import { Timeout } from "react-number-format/types/types";
import { Outlet } from "react-router-dom";
import cn from "classnames";
import { PageContent } from "../page-content/page-content";
import { PageFooter } from "../page-footer/page-footer";
import { PageHeader } from "../page-header/page-header";
import styles from "./page-layout.module.scss";

interface IPageLayout {
    isExternal: boolean;
}

export const PageLayout = React.memo(({ isExternal }: IPageLayout) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [blockScroll, setBlockScroll] = useState<boolean>(false);
    const [scrollTop, setScrollTop] = useState<number>(0);
    const debouncedScrollTop = useDebounce(scrollTop, 500);
    const timeoutRef = useRef<Timeout>();

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.blockLayoutScroll, blockLayoutScroll);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.blockLayoutScroll, blockLayoutScroll);
        };
    }, [debouncedScrollTop]);

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.addEventListener("scroll", onScrollEvent);
        }
        return () => {
            if (containerRef.current) {
                containerRef.current.removeEventListener("scroll", onScrollEvent);
            }
        };
    }, [containerRef.current]);

    const onScrollEvent = (e: any) => {
        setScrollTop(e.target.scrollTop);
    };

    const blockLayoutScroll = () => {
        if (debouncedScrollTop === 0) {
            setBlockScroll(true);
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                setBlockScroll(false);
            }, 500);
        }
    };

    return (
        <div className={cn(styles.page, blockScroll ? styles.blockScroll : styles.allowScroll)} id="pageLayout"
             ref={containerRef}>
            {!isExternal && <DebugPanel/>}
            <PageHeader/>
            <PageContent>
                <Outlet/>
            </PageContent>
            <PageFooter isExternal={isExternal}/>
            <ToastContainer/>
        </div>
    );
});
